import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of, throwError } from 'rxjs';
import { SourceDataObject, Tenant, VcsBranch, VcsRepository, VcsSource } from '../model/tenants/tenant.model';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { UserProfile } from '../model/tenants/user-profile.model';
import { ModelStatus, ModelUpgradeDownload } from '../model/tenants/model-status.model';
import { EventSourcePolyfill } from 'event-source-polyfill';

/** The filename of an `attachment; filename="x.zip"` header, or null when absent
 *  (also when CORS does not expose the header to the page). */
export function filenameFromContentDisposition(header: string | null): string | null {
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1].trim()) : null;
}

export interface ClassInfo {
  name: string;
  pluralName: string;
  kind: string;
  fields: { name: string; dataType: string; required: boolean }[];
}

@Injectable({
  providedIn: 'root',
})
export class TenantClientService {
  constructor(public auth: AuthService, private http: HttpClient) {}

  getTenants(): Observable<Tenant[]> {
    return this.http.get<Tenant[]>(environment.endpoints.tenants);
  }

  getTenant(tenantCode: string): Observable<Tenant> {
    return this.http.get<Tenant>(
      `${environment.endpoints.tenants}/${tenantCode}`
    );
  }

  getUserProfile(tenantCode: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(
      `${environment.endpoints.accounts}/${tenantCode}/me`
    );
  }

  rebuild(tenant: string) {
    return this.http.post<void>(
      `${environment.endpoints.admin}/${tenant}/rebuild`,
      {}
    );
  }

  /** Remedy for 'unresolved' ACL rows: recomputes every object's access-control rows from
   *  the current model, resolving rows left pointing at a node a rename or removal dropped. */
  rebuildAccessControl(tenant: string) {
    return this.http.post<void>(
      `${environment.endpoints.admin}/${tenant}/rebuildAccessControl`,
      {}
    );
  }

  loadDefaultData(tenant: string) {
    return this.http.post<void>(
      `${environment.endpoints.data}/${tenant}/loadDefaultData`,
      {}
    );
  }

  loadAssets(tenant: string) {
    return this.http.post<void>(
      `${environment.endpoints.metadata}/${tenant}/downloadAssets`,
      {}
    );
  }

  initializeStorage(tenant: string) {
    return this.http.get<void>(
      `${environment.endpoints.tenants}/${tenant}/initializeStorage`
    );
  }

  deleteTenant(tenant: string, name: string) {
    return this.http.delete<void>(`${environment.endpoints.admin}/${tenant}?deleteTenant=true&name=${encodeURIComponent(name)}`);
  }

  deleteTenantData(tenant: string, name: string) {
    return this.http.delete<void>(`${environment.endpoints.admin}/${tenant}?name=${encodeURIComponent(name)}`);
  }

  createTenant(value: any): Observable<string> {
    return this.http
      .post(environment.endpoints.tenants, value, { observe: 'response' })
      .pipe(map((response) => response.headers.get('location') || ''));
  }

  getVcsSources(): Observable<VcsSource[]> {
    return this.http.get<VcsSource[]>(`${environment.endpoints.tenants}/vcsSources`);
  }

  getRepositories(gitSource: string): Observable<VcsRepository[]> {
    return this.http.get<VcsRepository[]>(
      `${environment.endpoints.tenants}/vcsSources/${gitSource}/repositories`
    );
  }

  getBranches(gitSource: string, gitRepository: string): Observable<VcsBranch[]> {
    return this.http.get<VcsBranch[]>(
      `${environment.endpoints.tenants}/vcsSources/${gitSource}/repositories/${gitRepository}/branches`
    );
  }

  getDefaultSourceDataObject(gitSource?: string, gitRepository?: string, branch?: string): Observable<SourceDataObject> {
    const params: Record<string, string> = {};
    if (gitSource) params['gitSource'] = gitSource;
    if (gitRepository) params['gitRepository'] = gitRepository;
    if (branch) params['branch'] = branch;
    return this.http.get<SourceDataObject>(
      `${environment.endpoints.tenants}/defaultSourceDataObject`,
      { params }
    );
  }

  getClasses(tenant: string, basicOnly = true): Observable<ClassInfo[]> {
    const params = basicOnly ? '?kind=BASIC' : '';
    return this.http.get<ClassInfo[]>(
      `${environment.endpoints.metadata}/${tenant}/classes${params}`
    );
  }

  recalculate(tenant: string, pluralName: string): Observable<void> {
    return this.http.post<void>(
      `${environment.endpoints.admin}/${tenant}/${pluralName}/recalculate`,
      null
    );
  }

  sync(tenant: string, typeName: string): Observable<void> {
    return this.http.post<void>(
      `${environment.endpoints.admin}/${tenant}/${typeName}/sync`,
      null
    );
  }

  getModelStatus(tenant: string): Observable<ModelStatus> {
    return this.http.get<ModelStatus>(
      `${environment.endpoints.admin}/${tenant}/modelStatus`
    );
  }

  /** Reloads the data model. A refused model answers 422 with the same body as modelStatus;
   *  that is a result to show, not a failure, so it is returned as a status. */
  reloadModel(tenant: string): Observable<ModelStatus> {
    return this.http
      .post<ModelStatus>(`${environment.endpoints.admin}/${tenant}/reloadModel`, {})
      .pipe(
        catchError((error: HttpErrorResponse) =>
          error.status === 422 && error.error?.state
            ? of(error.error as ModelStatus)
            : throwError(() => error)
        )
      );
  }

  /** The upgraded definition files as a zip. Fetched through HttpClient so the Bearer token
   *  is attached; a plain link would not carry it. */
  downloadModelUpgrade(tenant: string): Observable<ModelUpgradeDownload> {
    return this.http
      .get(`${environment.endpoints.admin}/${tenant}/modelUpgrade`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response) => ({
          blob: response.body as Blob,
          filename:
            filenameFromContentDisposition(response.headers.get('Content-Disposition')) ??
            `${tenant}-definition-upgrade.zip`,
        }))
      );
  }

  getEventSource(tenant: string): Observable<EventSource> {
    return this.auth.getAccessTokenSilently().pipe(
      map((token) => {
        const eventSourceInitDict = {
          headers: { Authorization: `Bearer ${token}` },
        };
        return new EventSourcePolyfill(
          `${environment.endpoints.data}/${tenant}/eventStream`,
          eventSourceInitDict
        );
      })
    );
  }
}
